import { useCallback, useEffect, useState } from 'react';
import { api, fmt, fmtDate, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import { useList } from '../lib/useList.ts';
import { invalidateOptions } from '../options.ts';
import { initialsOf, invalidateCompany, loadCompany, logoUrl, type Company }
  from '../company.ts';
import PasswordInput from '../components/PasswordInput.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';

type Tab = 'company' | 'options' | 'machines' | 'shifts' | 'stages'
  | 'suppliers' | 'job-workers' | 'consumables' | 'costing-rates' | 'users';

export default function Settings({ user, view }: { user: User; view: Tab }) {
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const can = (p: string) => user.permissions?.includes(p) ?? false;

  useEffect(() => { setError(''); setNotice(''); }, [view]);

  return (
    <div className="panel">
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      {view === 'options' && (
        <Options canManage={can('settings_options.manage')}
                 setError={setError} setNotice={setNotice} />
      )}
      {view === 'machines' && (
        <Machines canManage={can('settings_machines.manage')}
                  setError={setError} setNotice={setNotice} />
      )}
      {view === 'shifts' && (
        <Shifts canManage={can('settings_shifts.manage')}
                setError={setError} setNotice={setNotice} />
      )}
      {view === 'stages' && (
        <Stages canManage={can('settings_stages.manage')}
                setError={setError} setNotice={setNotice} />
      )}
      {view === 'suppliers' && (
        <Suppliers canManage={can('settings_suppliers.manage')}
                   setError={setError} setNotice={setNotice} />
      )}
      {view === 'job-workers' && (
        <JobWorkers canManage={can('settings_suppliers.manage')}
                    setError={setError} setNotice={setNotice} />
      )}
      {view === 'consumables' && (
        <Consumables canManage={can('settings_consumables.manage')}
                     setError={setError} setNotice={setNotice} />
      )}
      {view === 'company' && (
        <CompanyDetails canManage={can('settings_company.manage')}
                        setError={setError} setNotice={setNotice} />
      )}
      {view === 'costing-rates' && (
        <CostingRates canManage={can('settings_costing_rates.manage')}
                      setError={setError} setNotice={setNotice} />
      )}
      {view === 'users' && (
        <Users user={user} canManage={can('settings_users.manage')}
               setError={setError} setNotice={setNotice} />
      )}
    </div>
  );
}

type S = { canManage: boolean; setError: (s: string) => void; setNotice: (s: string) => void };

/** Every master screen shows what depends on a row, so nobody turns one off blind. */
const onOff = (active: boolean) => (
  <span className={`tag ${active ? 'ready' : 'blocked'}`}>{active ? 'In use' : 'Off'}</span>
);

/* --------------------------------------------------------------- options */

interface OptionRow {
  id: number; list_key: string; list_label: string;
  value: string; sort_order: number; active: boolean;
}
interface OptionListMeta { key: string; label: string; hint: string | null; values: string }

/**
 * The waste allowance (058): boards spoiled per job in setting up and
 * running. Every new card's run carries that many boards' worth of extra
 * boxes, so the sheets worked out from it include the waste.
 */
function WasteAllowance({ canManage, setError, setNotice }: S) {
  const [saved, setSaved] = useState<number | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ defaults: { wasteAllowanceSheets: number } }>('/api/settings/defaults')
      .then((r) => {
        setSaved(r.defaults.wasteAllowanceSheets);
        setValue(String(r.defaults.wasteAllowanceSheets));
      })
      .catch(() => setError('Could not load the waste allowance'));
  }, [setError]);

  async function save() {
    setBusy(true); setError('');
    try {
      const r = await api.patch<{ defaults: { wasteAllowanceSheets: number } }>(
        '/api/settings/defaults', { wasteAllowanceSheets: Number(value) });
      setSaved(r.defaults.wasteAllowanceSheets);
      setNotice(`New job cards now carry ${fmt(String(r.defaults.wasteAllowanceSheets))} waste boards`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not save the allowance'); }
    finally { setBusy(false); }
  }

  if (saved === null) return null;
  const n = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 1000;
  const shown = valid ? n : saved;
  return (
    <div className="add-row">
      <div className="field" style={{ width: 190 }}>
        <label htmlFor="waste-allowance">Waste boards per job</label>
        {canManage ? (
          <input id="waste-allowance" type="number" min="0" max="1000" step="1" value={value}
                 style={{ width: 110, textAlign: 'right' }}
                 onChange={(e) => setValue(e.target.value)} />
        ) : <div style={{ padding: '7px 0', fontWeight: 600 }}>{fmt(String(saved))}</div>}
      </div>
      <div className="hint" style={{ flex: 1, alignSelf: 'center' }}>
        Added to every new job card. An order of 10,000 at 2 ups runs {fmt(String(10000 + 2 * shown))}:
        {' '}{fmt(String(5000 + shown))} sheets.
      </div>
      {canManage && (
        <button className="btn primary" disabled={busy || !valid || n === saved} onClick={save}>
          Save
        </button>
      )}
    </div>
  );
}

function Options({ canManage, setError, setNotice }: S) {
  const list = useList<OptionRow>('/api/settings/options', 'options',
                                  { sort: 'list_label' });
  const [lists, setLists] = useState<OptionListMeta[]>([]);
  const [form, setForm] = useState({ listKey: '', value: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ lists: OptionListMeta[] }>('/api/settings/option-lists')
      .then((r) => {
        setLists(r.lists);
        setForm((p) => (p.listKey ? p : { ...p, listKey: r.lists[0]?.key ?? '' }));
      })
      .catch(() => setError('Could not load the option lists'));
  }, [setError]);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post(`/api/settings/options/${form.listKey}`, { value: form.value.trim() });
      setNotice(`${form.value.trim()} added`);
      setForm((p) => ({ ...p, value: '' }));
      invalidateOptions();
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add the value'); }
    finally { setBusy(false); }
  }

  async function toggle(r: OptionRow) {
    setError('');
    try {
      await api.patch(`/api/settings/options/${r.id}`, { active: !r.active });
      invalidateOptions();
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the value'); }
  }

  async function rename(r: OptionRow) {
    const next = window.prompt(`Rename "${r.value}"`, r.value);
    if (next === null || !next.trim() || next.trim() === r.value) return;
    setError('');
    try {
      await api.patch(`/api/settings/options/${r.id}`, { value: next.trim() });
      setNotice(`Renamed to ${next.trim()}`);
      invalidateOptions();
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not rename the value'); }
  }

  const columns: Column<OptionRow>[] = [
    { key: 'list_label', label: 'List', type: 'choice',
      choices: lists.map((l) => ({ value: l.key, label: l.label })),
      render: (r) => r.list_label },
    { key: 'value', label: 'Value' },
    { key: 'sort_order', label: 'Order', type: 'number', align: 'right', secondary: true },
    { key: 'active', label: 'Status', type: 'bool', render: (r) => onOff(r.active) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (canManage ? (
        <>
          <button className="btn" onClick={() => rename(r)}>Rename</button>
          <button className="btn" style={{ marginLeft: 6 }} onClick={() => toggle(r)}>
            {r.active ? 'Turn off' : 'Turn on'}
          </button>
        </>
      ) : null),
    },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          The values every form offers. Turning one off keeps it on the orders
          that already use it and only removes it from the pickers.
        </span>
      </div>
      <WasteAllowance canManage={canManage} setError={setError} setNotice={setNotice} />
      {canManage && (
        <div className="add-row">
          <div className="field" style={{ width: 190 }}>
            <label className="req">List</label>
            <select value={form.listKey}
                    onChange={(e) => setForm((p) => ({ ...p, listKey: e.target.value }))}>
              {lists.map((l) => (
                <option key={l.key} value={l.key}>{l.label} ({l.values})</option>
              ))}
            </select>
            <div className="hint">
              {lists.find((l) => l.key === form.listKey)?.hint ?? ''}
            </div>
          </div>
          <div className="field">
            <label className="req">New value</label>
            <input type="text" value={form.value}
                   onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))} />
          </div>
          <button className="btn primary"
                  disabled={busy || !form.value.trim() || !form.listKey} onClick={add}>
            Add value
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/options"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No values yet." />
    </>
  );
}

/* -------------------------------------------------------------- machines */

interface Machine {
  id: number; name: string; code: string | null; notes: string | null;
  active: boolean; entry_count: string;
}

function Machines({ canManage, setError, setNotice }: S) {
  const list = useList<Machine>('/api/settings/machines', 'machines', { sort: 'name' });
  const [form, setForm] = useState({ name: '', code: '' });
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post('/api/settings/machines', {
        name: form.name.trim(),
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
      });
      setNotice(`${form.name.trim()} added`);
      setForm({ name: '', code: '' });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add the machine'); }
    finally { setBusy(false); }
  }

  async function toggle(r: Machine) {
    setError('');
    try {
      await api.patch(`/api/settings/machines/${r.id}`, { active: !r.active });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the machine'); }
  }

  const columns: Column<Machine>[] = [
    { key: 'name', label: 'Machine' },
    { key: 'code', label: 'Code', render: (r) => <span className="dim">{r.code ?? '—'}</span> },
    { key: 'notes', label: 'Notes', secondary: true,
      render: (r) => <span className="dim">{r.notes ?? '—'}</span> },
    { key: 'entry_count', label: 'Entries', type: 'number', align: 'right',
      render: (r) => <span className="dim">{fmt(r.entry_count)}</span> },
    { key: 'active', label: 'Status', type: 'bool', render: (r) => onOff(r.active) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (canManage ? (
        <button className="btn" onClick={() => toggle(r)}>
          {r.active ? 'Turn off' : 'Turn on'}
        </button>
      ) : null),
    },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          Machines are recorded against every production entry, which is what
          makes machine-wise reporting possible. Turning one off keeps its history.
        </span>
      </div>
      {canManage && (
        <div className="add-row">
          <div className="field">
            <label className="req">Machine name</label>
            <input type="text" value={form.name}
                   onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 140 }}>
            <label>Short code</label>
            <input type="text" value={form.code}
                   onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
          </div>
          <button className="btn primary" disabled={busy || !form.name.trim()} onClick={add}>
            Add machine
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/machines"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No machines yet." />
    </>
  );
}

/* ---------------------------------------------------------------- shifts */

interface Shift {
  id: number; name: string; starts_at: string; ends_at: string;
  crosses_midnight: boolean; active: boolean; entry_count: string;
}

function Shifts({ canManage, setError, setNotice }: S) {
  const list = useList<Shift>('/api/settings/shifts', 'shifts', { sort: 'starts_at' });
  const [form, setForm] = useState({ name: '', startsAt: '08:00', endsAt: '20:00' });
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post('/api/settings/shifts', {
        name: form.name.trim(), startsAt: form.startsAt, endsAt: form.endsAt,
      });
      setNotice(`${form.name.trim()} added`);
      setForm({ name: '', startsAt: '08:00', endsAt: '20:00' });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add the shift'); }
    finally { setBusy(false); }
  }

  async function toggle(r: Shift) {
    setError('');
    try {
      await api.patch(`/api/settings/shifts/${r.id}`, { active: !r.active });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the shift'); }
  }

  const columns: Column<Shift>[] = [
    { key: 'name', label: 'Shift' },
    { key: 'starts_at', label: 'Starts', render: (r) => r.starts_at.slice(0, 5) },
    { key: 'ends_at', label: 'Ends', render: (r) => r.ends_at.slice(0, 5) },
    { key: 'crosses_midnight', label: 'Crosses midnight', type: 'bool', secondary: true,
      render: (r) => (r.crosses_midnight
        ? <span className="tag soon">Yes</span> : <span className="dim">No</span>) },
    { key: 'entry_count', label: 'Entries', type: 'number', align: 'right',
      render: (r) => <span className="dim">{fmt(r.entry_count)}</span> },
    { key: 'active', label: 'Status', type: 'bool', render: (r) => onOff(r.active) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (canManage ? (
        <button className="btn" onClick={() => toggle(r)}>
          {r.active ? 'Turn off' : 'Turn on'}
        </button>
      ) : null),
    },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          A shift that ends before it starts crosses midnight — that is worked
          out from the times, not ticked, and it decides which day a night
          shift's output counts against.
        </span>
      </div>
      {canManage && (
        <div className="add-row">
          <div className="field">
            <label className="req">Shift name</label>
            <input type="text" placeholder="First shift" value={form.name}
                   onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 130 }}>
            <label>Starts</label>
            <input type="time" value={form.startsAt}
                   onChange={(e) => setForm((p) => ({ ...p, startsAt: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 130 }}>
            <label>Ends</label>
            <input type="time" value={form.endsAt}
                   onChange={(e) => setForm((p) => ({ ...p, endsAt: e.target.value }))} />
          </div>
          <button className="btn primary" disabled={busy || !form.name.trim()} onClick={add}>
            Add shift
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/shifts"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No shifts yet." />
    </>
  );
}

/* ---------------------------------------------------------------- stages */

interface Stage {
  id: number; name: string; sequence_no: number; active: boolean;
  is_dispatch: boolean; always_included: boolean; asks_quantity: boolean;
  card_part: 'PRINTING' | 'CORRUGATION' | null; card_required: boolean;
  used_count: string; waiting_count: string; requires: number[];
}

const CARD_LABEL = { PRINTING: 'Printing card', CORRUGATION: 'Corrugation card' } as const;

interface StageDraft {
  id: number | null; name: string; afterStageId: string; cardPart: '' | 'PRINTING' | 'CORRUGATION';
  cardRequired: boolean; asksQuantity: boolean; requires: number[];
}

function Stages({ canManage, setError, setNotice }: S) {
  const [rows, setRows] = useState<Stage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<StageDraft | null>(null);
  const [switching, setSwitching] = useState<Stage | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ stages: Stage[] }>('/api/settings/stages?pageSize=200&sort=sequence_no');
      setRows(r.stages);
      setLoaded(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load the stages'); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  const nameOf = new Map(rows.map((r) => [r.id, r.name]));
  const fixed = rows.filter((r) => r.always_included || r.is_dispatch);
  const movable = rows.filter((r) => !r.always_included && !r.is_dispatch);

  async function run(work: () => Promise<unknown>, done: string, fallback: string) {
    setBusy(true); setError('');
    try {
      await work();
      setNotice(done);
      await load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback);
      return false;
    } finally { setBusy(false); }
  }

  const move = (r: Stage, direction: 'up' | 'down') =>
    run(() => api.post(`/api/settings/stages/${r.id}/move`, { direction }),
      `${r.name} moved ${direction}`, 'Could not move the stage');

  async function save() {
    if (!draft) return;
    const body = {
      name: draft.name.trim(),
      cardPart: draft.cardPart || null,
      cardRequired: draft.cardPart ? draft.cardRequired : false,
      asksQuantity: draft.asksQuantity,
      requires: draft.requires,
    };
    const ok = draft.id === null
      ? await run(() => api.post('/api/settings/stages', {
          ...body, afterStageId: draft.afterStageId ? Number(draft.afterStageId) : null,
        }), `${body.name} added`, 'Could not add the stage')
      : await run(() => api.patch(`/api/settings/stages/${draft.id}`, body),
          `${body.name} saved`, 'Could not save the stage');
    if (ok) setDraft(null);
  }

  async function confirmSwitch() {
    if (!switching) return;
    const on = !switching.active;
    const ok = await run(() => api.patch(`/api/settings/stages/${switching.id}`, { active: on }),
      `${switching.name} switched ${on ? 'on' : 'off'}`, 'Could not switch the stage');
    if (ok) setSwitching(null);
  }

  const openAdd = () => setDraft({
    id: null, name: '', afterStageId: String(movable.filter((r) => r.active).at(-1)?.id ?? ''),
    cardPart: '', cardRequired: false, asksQuantity: false, requires: [],
  });
  const openEdit = (r: Stage) => setDraft({
    id: r.id, name: r.name, afterStageId: '', cardPart: r.card_part ?? '',
    cardRequired: r.card_required, asksQuantity: r.asks_quantity, requires: r.requires,
  });

  const options = (r: Stage) => {
    const tags = [
      r.always_included && !r.is_dispatch && 'On every card',
      r.is_dispatch && 'Dispatch',
      r.asks_quantity && 'Asks quantity',
    ].filter(Boolean) as string[];
    return tags.length
      ? tags.map((t) => <span key={t} className="tag ready" style={{ marginRight: 4 }}>{t}</span>)
      : <span className="dim">—</span>;
  };

  const renderRow = (r: Stage, i: number, list: Stage[]) => {
    const isFixed = r.always_included || r.is_dispatch;
    return (
      <tr key={r.id} className={r.active ? '' : 'struck'}>
        <td className="num dim">{r.sequence_no}</td>
        <td><strong style={{ fontWeight: 600 }}>{r.name}</strong></td>
        <td>
          {r.card_part
            ? <>{CARD_LABEL[r.card_part]}{r.card_required && <div className="dim" style={{ fontSize: 12 }}>Must be filled before release</div>}</>
            : <span className="dim">—</span>}
        </td>
        <td>
          {r.requires.length
            ? r.requires.map((id) => nameOf.get(id)).join(', ')
            : <span className="dim">—</span>}
        </td>
        <td>{options(r)}</td>
        <td className="num dim">{fmt(r.used_count)}</td>
        <td>{onOff(r.active)}</td>
        {canManage && (
          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
            {isFixed ? (
              <span className="dim" style={{ fontSize: 12 }}>Fixed at the end</span>
            ) : (
              <>
                <button className="btn" aria-label={`Move ${r.name} up`} title="Move up"
                        disabled={busy || i === 0} onClick={() => void move(r, 'up')}>↑</button>
                <button className="btn" aria-label={`Move ${r.name} down`} title="Move down"
                        style={{ marginLeft: 4 }}
                        disabled={busy || i === list.length - 1} onClick={() => void move(r, 'down')}>↓</button>
                <button className="btn" style={{ marginLeft: 8 }} disabled={busy}
                        onClick={() => openEdit(r)}>Edit</button>
                <button className="btn" style={{ marginLeft: 4 }} disabled={busy}
                        onClick={() => setSwitching(r)}>{r.active ? 'Switch off' : 'Switch on'}</button>
              </>
            )}
          </td>
        )}
      </tr>
    );
  };

  const cols = canManage ? 8 : 7;
  const others = draft ? movable.filter((r) => r.id !== draft.id && r.active) : [];

  return (
    <>
      <div className="stagebar" style={{ justifyContent: 'space-between' }}>
        <span className="hint" style={{ maxWidth: 720 }}>
          The order work moves through the factory. Each job card ticks the stages it
          needs and they run in this order; Packing and Dispatch close every card.
          Changes here only affect new cards — jobs already running keep their route.
        </span>
        {canManage && (
          <button className="btn primary" disabled={busy || !loaded} onClick={openAdd}>Add stage</button>
        )}
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Stage</th>
            <th>Works from</th>
            <th>Needs first</th>
            <th>Options</th>
            <th className="num">Used on</th>
            <th>Status</th>
            {canManage && <th />}
          </tr>
        </thead>
        <tbody>
          {movable.map((r, i, list) => renderRow(r, i, list))}
          {fixed.length > 0 && (
            <tr><td colSpan={cols} className="dim" style={{ fontSize: 12, paddingTop: 14 }}>On every card</td></tr>
          )}
          {fixed.map((r, i, list) => renderRow(r, i, list))}
          {loaded && rows.length === 0 && (
            <tr><td colSpan={cols} className="dim">No stages.</td></tr>
          )}
        </tbody>
      </table>

      {draft && (
        <ConfirmDialog
          title={draft.id === null ? 'Add a stage' : `Edit ${nameOf.get(draft.id) ?? 'stage'}`}
          subtitle={draft.id === null
            ? 'New cards can tick it straight away. Jobs already running are not changed.'
            : 'Only new cards see the change. Jobs already running keep their route.'}
          confirmLabel={draft.id === null ? 'Add stage' : 'Save'}
          cancelLabel="Cancel" busy={busy} focusConfirm={false}
          confirmDisabled={!draft.name.trim()}
          onConfirm={() => void save()}
          onCancel={() => setDraft(null)}>
          <div className="field">
            <label className="req">Name</label>
            <input type="text" value={draft.name} autoFocus placeholder="Varnish"
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          {draft.id === null && (
            <div className="field">
              <label>Comes after</label>
              <select value={draft.afterStageId}
                      onChange={(e) => setDraft({ ...draft, afterStageId: e.target.value })}>
                <option value="">— first, before everything —</option>
                {movable.filter((r) => r.active).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label>Works from</label>
            <select value={draft.cardPart}
                    onChange={(e) => setDraft({ ...draft, cardPart: e.target.value as StageDraft['cardPart'],
                      cardRequired: e.target.value ? draft.cardRequired : false })}>
              <option value="">No card part — just the stage</option>
              <option value="PRINTING">Printing card</option>
              <option value="CORRUGATION">Corrugation card</option>
            </select>
          </div>
          <label className="check-line">
            <input type="checkbox" checked={draft.cardRequired} disabled={!draft.cardPart}
                   onChange={(e) => setDraft({ ...draft, cardRequired: e.target.checked })} />
            That card must be filled in before the job is released
          </label>
          <label className="check-line">
            <input type="checkbox" checked={draft.asksQuantity}
                   onChange={(e) => setDraft({ ...draft, asksQuantity: e.target.checked })} />
            Asks how many were run when the stage is finished
          </label>
          {others.length > 0 && (
            <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
              <label>Needs first</label>
              <div className="stages">
                {others.map((r) => (
                  <label key={r.id}>
                    <input type="checkbox" checked={draft.requires.includes(r.id)}
                           onChange={() => setDraft({ ...draft, requires: draft.requires.includes(r.id)
                             ? draft.requires.filter((x) => x !== r.id) : [...draft.requires, r.id] })} />
                    {r.name}
                  </label>
                ))}
              </div>
              <div className="hint" style={{ marginTop: 6 }}>
                A card that ticks this stage must also tick these, and they come earlier.
              </div>
            </div>
          )}
        </ConfirmDialog>
      )}

      {switching && (
        <ConfirmDialog
          title={`Switch ${switching.name} ${switching.active ? 'off' : 'on'}?`}
          confirmLabel={switching.active ? 'Switch off' : 'Switch on'}
          cancelLabel="Cancel" busy={busy}
          onConfirm={() => void confirmSwitch()} onCancel={() => setSwitching(null)}>
          {switching.active ? (
            <p style={{ margin: 0 }}>
              New cards stop offering it.
              {Number(switching.waiting_count) > 0
                ? ` ${fmt(switching.waiting_count)} running ${Number(switching.waiting_count) === 1 ? 'job still has' : 'jobs still have'} it on their route and will keep it.`
                : ' No running job has it on its route.'}
            </p>
          ) : (
            <p style={{ margin: 0 }}>New cards can tick it again, in its old place in the order.</p>
          )}
        </ConfirmDialog>
      )}
    </>
  );
}

/* ------------------------------------------------------------- suppliers */

interface Supplier {
  id: number; name: string; contact_name: string | null; phone: string | null;
  email: string | null; address: string | null; active: boolean;
  deliveries: string; sheets_supplied: string; last_delivery: string | null; reels: string;
  lead_days: number;
}

function Suppliers({ canManage, setError, setNotice }: S) {
  const list = useList<Supplier>('/api/settings/suppliers', 'suppliers', { sort: 'name' });
  const [form, setForm] = useState({ name: '', contactName: '', phone: '' });
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post('/api/settings/suppliers', {
        name: form.name.trim(),
        ...(form.contactName.trim() ? { contactName: form.contactName.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
      });
      setNotice(`${form.name.trim()} added`);
      setForm({ name: '', contactName: '', phone: '' });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add supplier'); }
    finally { setBusy(false); }
  }

  // Sets the expected date on a board order, and when it has to be ordered by.
  async function setLeadDays(r: Supplier) {
    const v = window.prompt(`Usual days for ${r.name} to deliver board after it is ordered`, String(r.lead_days));
    if (v === null || v.trim() === '' || Number(v) === r.lead_days) return;
    setError('');
    try {
      await api.patch(`/api/settings/suppliers/${r.id}`, { leadDays: Number(v) });
      setNotice(`${r.name} now takes ${Number(v)} days`);
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the delivery days'); }
  }

  async function toggle(r: Supplier) {
    setError('');
    try {
      await api.patch(`/api/settings/suppliers/${r.id}`, { active: !r.active });
      setNotice(`${r.name} ${r.active ? 'turned off' : 'turned back on'}`);
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update supplier'); }
  }

  const columns: Column<Supplier>[] = [
    { key: 'name', label: 'Supplier' },
    { key: 'contact_name', label: 'Contact',
      render: (r) => <span className="dim">{r.contact_name ?? '—'}</span> },
    { key: 'phone', label: 'Phone',
      render: (r) => <span className="dim">{r.phone ?? '—'}</span> },
    { key: 'email', label: 'Email', secondary: true,
      render: (r) => <span className="dim">{r.email ?? '—'}</span> },
    { key: 'lead_days', label: 'Delivery days', type: 'number', align: 'right',
      render: (r) => canManage
        ? <button className="linkish" title="Usual days from ordering board to it arriving"
                  onClick={() => void setLeadDays(r)}>{r.lead_days}</button>
        : <span className="dim">{r.lead_days}</span> },
    { key: 'deliveries', label: 'Board deliveries', type: 'number', align: 'right',
      render: (r) => <span className="dim">{fmt(r.deliveries)}</span> },
    { key: 'sheets_supplied', label: 'Sheets supplied', type: 'number', align: 'right',
      render: (r) => <span className="dim">{fmt(r.sheets_supplied)}</span> },
    { key: 'last_delivery', label: 'Last delivery', type: 'date',
      render: (r) => <span className="dim">{r.last_delivery ? fmtDate(r.last_delivery) : '—'}</span> },
    { key: 'reels', label: 'Reels', type: 'number', align: 'right', secondary: true,
      render: (r) => <span className="dim">{fmt(r.reels)}</span> },
    { key: 'active', label: 'Status', type: 'bool', render: (r) => onOff(r.active) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (canManage ? (
        <button className="btn" onClick={() => toggle(r)}>
          {r.active ? 'Turn off' : 'Turn on'}
        </button>
      ) : null),
    },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          Who board and consumables are bought from. Turning one off keeps every
          past purchase against it and only removes it from the pickers.
        </span>
      </div>
      {canManage && (
        <div className="add-row">
          <div className="field">
            <label className="req">Supplier name</label>
            <input type="text" value={form.name}
                   onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 170 }}>
            <label>Contact</label>
            <input type="text" value={form.contactName}
                   onChange={(e) => setForm((p) => ({ ...p, contactName: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 150 }}>
            <label>Phone</label>
            <input type="text" value={form.phone}
                   onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          </div>
          <button className="btn primary" disabled={busy || !form.name.trim()} onClick={add}>
            Add supplier
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/suppliers"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No suppliers yet." />
    </>
  );
}

/* ----------------------------------------------------------- job workers */

interface JobWorker {
  id: number; name: string; place: string | null; phone: string | null; active: boolean;
  live_trips: number; trips_done: number;
}

function JobWorkers({ canManage, setError, setNotice }: S) {
  const list = useList<JobWorker>('/api/settings/job-workers', 'workers', { sort: 'name' });
  const [form, setForm] = useState({ name: '', place: '', phone: '' });
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post('/api/settings/job-workers', {
        name: form.name.trim(),
        ...(form.place.trim() ? { place: form.place.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
      });
      setNotice(`${form.name.trim()} added`);
      setForm({ name: '', place: '', phone: '' });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add the job worker'); }
    finally { setBusy(false); }
  }

  async function toggle(r: JobWorker) {
    setError('');
    try {
      await api.patch(`/api/settings/job-workers/${r.id}`, { active: !r.active });
      setNotice(`${r.name} ${r.active ? 'turned off' : 'turned back on'}`);
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the job worker'); }
  }

  const columns: Column<JobWorker>[] = [
    { key: 'name', label: 'Job worker' },
    { key: 'place', label: 'Place', render: (r) => <span className="dim">{r.place ?? '—'}</span> },
    { key: 'phone', label: 'Phone', render: (r) => <span className="dim">{r.phone ?? '—'}</span> },
    { key: 'live_trips', label: 'Out now', type: 'number', align: 'right',
      render: (r) => <span className="dim">{fmt(r.live_trips)}</span> },
    { key: 'trips_done', label: 'Trips done', type: 'number', align: 'right', secondary: true,
      render: (r) => <span className="dim">{fmt(r.trips_done)}</span> },
    { key: 'active', label: 'Status', type: 'bool', render: (r) => onOff(r.active) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (canManage ? (
        <button className="btn" onClick={() => void toggle(r)}>{r.active ? 'Turn off' : 'Turn on'}</button>
      ) : null),
    },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          Who takes work outside: a stage, or a whole order, when Opal is busy, a machine is down, the size
          can't be done here or the machine isn't owned. Turning one off keeps every past trip against it.
        </span>
      </div>
      {canManage && (
        <div className="add-row">
          <div className="field">
            <label className="req">Name</label>
            <input type="text" value={form.name}
                   onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 170 }}>
            <label>Place</label>
            <input type="text" value={form.place}
                   onChange={(e) => setForm((p) => ({ ...p, place: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 150 }}>
            <label>Phone</label>
            <input type="text" value={form.phone}
                   onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          </div>
          <button className="btn primary" disabled={busy || !form.name.trim()} onClick={add}>
            Add job worker
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/job-workers"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No job workers yet." />
    </>
  );
}

/* ----------------------------------------------------------- consumables */

interface ConsumableMaster {
  id: number; name: string; category: string; unit: string;
  active: boolean; movements: string;
}

const CATEGORIES = ['GUM', 'PIN', 'CHEMICAL', 'INK', 'OTHER'];
const nice = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

function Consumables({ canManage, setError, setNotice }: S) {
  const list = useList<ConsumableMaster>('/api/settings/consumables', 'consumables',
                                         { sort: 'name' });
  const [form, setForm] = useState({ name: '', category: 'GUM', unit: '' });
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post('/api/settings/consumables', {
        name: form.name.trim(), category: form.category, unit: form.unit.trim(),
      });
      setNotice(`${form.name.trim()} added`);
      setForm({ name: '', category: 'GUM', unit: '' });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add consumable'); }
    finally { setBusy(false); }
  }

  const columns: Column<ConsumableMaster>[] = [
    { key: 'name', label: 'Item' },
    { key: 'category', label: 'Category', type: 'choice',
      choices: CATEGORIES.map((c) => ({ value: c, label: nice(c) })),
      render: (r) => <span className="dim">{nice(r.category)}</span> },
    { key: 'unit', label: 'Unit', render: (r) => <span className="dim">{r.unit}</span> },
    { key: 'movements', label: 'Movements', type: 'number', align: 'right',
      render: (r) => <span className="dim">{fmt(r.movements)}</span> },
    { key: 'active', label: 'Status', type: 'bool', render: (r) => onOff(r.active) },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          Gum, pins, ink and the rest. The unit is what a movement is counted in,
          so changing it later makes past movements mean something different —
          add a new item instead.
        </span>
      </div>
      {canManage && (
        <div className="add-row">
          <div className="field">
            <label className="req">Item name</label>
            <input type="text" value={form.name}
                   onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 150 }}>
            <label>Category</label>
            <select value={form.category}
                    onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{nice(c)}</option>)}
            </select>
          </div>
          <div className="field" style={{ width: 120 }}>
            <label className="req">Unit</label>
            <input type="text" placeholder="kg" value={form.unit}
                   onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))} />
          </div>
          <button className="btn primary"
                  disabled={busy || !form.name.trim() || !form.unit.trim()} onClick={add}>
            Add item
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/consumables"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No consumables yet." />
    </>
  );
}

/* ----------------------------------------------------------------- users */

interface AppUser {
  id: number; name: string; username: string; role: string;
  active: boolean; created_at: string; last_signed_in: string | null;
}
interface RoleOption { role_name: string; active: boolean }

/**
 * Accounts.
 *
 * Passwords are typed here and go straight to the server to be hashed. They are
 * never shown back, never logged, and changing one signs that person out
 * everywhere — otherwise "reset their password" leaves whoever is already
 * signed in exactly as signed in as before.
 */
function Users({
  user, canManage, setError, setNotice,
}: S & { user: User }) {
  const list = useList<AppUser>('/api/settings/users', 'users', { sort: 'name' });
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [form, setForm] = useState({ name: '', username: '', role: 'STAFF', password: '' });
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    api.get<{ roles: RoleOption[] }>('/api/roles')
      .then((r) => setRoles(r.roles.filter((x) => x.active)))
      // Somebody may manage users without being allowed to see roles; the three
      // seeded names are enough to keep the screen working.
      .catch(() => setRoles([{ role_name: 'ADMIN', active: true },
                             { role_name: 'MANAGER', active: true },
                             { role_name: 'STAFF', active: true }]));
  }, []);

  const change = useCallback(async (
    r: AppUser, patch: Record<string, unknown>, msg: string,
  ) => {
    setError('');
    try {
      await api.patch(`/api/settings/users/${r.id}`, patch);
      setNotice(msg);
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the user'); }
  }, [list, setError, setNotice]);

  async function add() {
    setBusy(true); setError('');
    try {
      await api.post('/api/settings/users', {
        name: form.name.trim(), username: form.username.trim().toLowerCase(),
        role: form.role, password: form.password,
      });
      setNotice(`${form.username.trim()} can now sign in`);
      setForm({ name: '', username: '', role: 'STAFF', password: '' });
      await list.reload();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add the user'); }
    finally { setBusy(false); }
  }

  async function resetPassword(r: AppUser) {
    if (newPassword.length < 10) { setError('Use at least 10 characters'); return; }
    await change(r, { password: newPassword },
                 `${r.username} has a new password and has been signed out everywhere`);
    setResetting(null);
    setNewPassword('');
  }

  const columns: Column<AppUser>[] = [
    { key: 'name', label: 'Name',
      render: (r) => (
        <>{r.name}{r.id === user.id && <span className="dim"> · you</span>}</>
      ) },
    { key: 'username', label: 'Username',
      render: (r) => <span className="mono">{r.username}</span> },
    { key: 'role', label: 'Role', type: 'choice',
      choices: roles.map((x) => ({ value: x.role_name, label: x.role_name })),
      render: (r) => (
        <select value={r.role} disabled={!canManage || r.id === user.id}
                title={r.id === user.id ? 'You cannot change your own role' : undefined}
                onChange={(e) => change(r, { role: e.target.value },
                                        `${r.username} is now ${e.target.value}`)}>
          {roles.map((x) => <option key={x.role_name} value={x.role_name}>{x.role_name}</option>)}
        </select>
      ) },
    { key: 'last_signed_in', label: 'Last signed in', type: 'date',
      render: (r) => (
        <span className="dim">
          {r.last_signed_in
            ? new Date(r.last_signed_in).toLocaleDateString('en-IN',
                { day: '2-digit', month: 'short', year: '2-digit' })
            : 'never'}
        </span>
      ) },
    { key: 'active', label: 'Status', type: 'bool',
      render: (r) => (
        <span className={`tag ${r.active ? 'ready' : 'blocked'}`}>
          {r.active ? 'Active' : 'Off'}
        </span>
      ) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => {
        if (!canManage) return null;
        if (resetting === r.id) {
          return (
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <PasswordInput autoFocus autoComplete="new-password"
                             placeholder="New password" value={newPassword}
                             style={{ width: 190 }} onChange={setNewPassword} />
              <button className="btn primary" onClick={() => resetPassword(r)}>Set</button>
              <button className="btn"
                      onClick={() => { setResetting(null); setNewPassword(''); }}>Cancel</button>
            </span>
          );
        }
        return (
          <>
            <button className="btn" onClick={() => { setNewPassword(''); setResetting(r.id); }}>
              Reset password
            </button>
            {r.id !== user.id && (
              <button className="btn" style={{ marginLeft: 6 }}
                      onClick={() => change(r, { active: !r.active },
                        `${r.username} ${r.active ? 'turned off' : 'turned back on'}`)}>
                {r.active ? 'Turn off' : 'Turn on'}
              </button>
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          Passwords are set here and never shown again. Changing one, or turning
          an account off, signs that person out everywhere straight away.
        </span>
      </div>
      {canManage && (
        <div className="add-row">
          <div className="field">
            <label className="req">Full name</label>
            <input type="text" value={form.name}
                   onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 160 }}>
            <label className="req">Username</label>
            <input type="text" value={form.username} autoComplete="off"
                   onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} />
          </div>
          <div className="field" style={{ width: 150 }}>
            <label>Role</label>
            <select value={form.role}
                    onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
              {roles.map((r) => (
                <option key={r.role_name} value={r.role_name}>{r.role_name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ width: 190 }}>
            <label className="req">Password</label>
            <PasswordInput value={form.password} autoComplete="new-password"
                           onChange={(v) => setForm((p) => ({ ...p, password: v }))} />
            <div className="hint">
              {form.password && form.password.length < 10
                ? `${10 - form.password.length} more characters`
                : 'At least 10 characters'}
            </div>
          </div>
          <button className="btn primary"
                  disabled={busy || !form.name.trim() || form.username.trim().length < 3
                            || form.password.length < 10}
                  onClick={add}>
            Add user
          </button>
        </div>
      )}
      <DataTable list={list} columns={columns} exportName="/api/settings/users"
                 rowKey={(r) => r.id} rowClass={(r) => (r.active ? '' : 'struck')}
                 empty="No users yet." />
    </>
  );
}

/* --------------------------------------------------------- costing rates */

interface CostingRate {
  key: string; label: string; unit: string; value: string; updated_at: string;
}

/**
 * What Opal charges, in one place.
 *
 * These used to live in two worse places: seven as constants in the costing
 * screen's source, which only a deploy could change and nothing recorded, and
 * seven as nothing at all, retyped on every costing so two estimators could
 * quietly disagree about the same box.
 *
 * Changing a rate here changes what a NEW costing starts from. Every costing
 * already saved keeps the rates it was quoted on, because it stores its own
 * copy -- so nothing already sent to a customer moves when a price does.
 */
const DEMY_KEYS = ['demyMaxShortIn', 'demyMaxLongIn'];

function CostingRates({ canManage, setError, setNotice }: {
  canManage: boolean;
  setError: (m: string) => void;
  setNotice: (m: string) => void;
}) {
  const [rates, setRates] = useState<CostingRate[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ rates: CostingRate[] }>('/api/settings/costing-rates');
      setRates(r.rates);
      setDraft(Object.fromEntries(r.rates.map((x) => [x.key, trimZeros(x.value)])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the rates');
    }
  }, [setError]);

  useEffect(() => { void load(); }, [load]);

  async function save(rate: CostingRate) {
    const typed = (draft[rate.key] ?? '').trim();
    const value = Number(typed);
    if (typed === '' || !Number.isFinite(value) || value < 0) {
      setError(`${rate.label} must be a figure of zero or more.`);
      return;
    }
    if (DEMY_KEYS.includes(rate.key) && value === 0) {
      setError('A Demy sheet side must be more than 0 inches.');
      return;
    }
    if (value === Number(rate.value)) return;

    setBusy(rate.key); setError(''); setNotice('');
    try {
      await api.patch(`/api/settings/costing-rates/${rate.key}`, { value });
      const said = DEMY_KEYS.includes(rate.key)
        ? `Demy sheet is now up to ${trimZeros(draft.demyMaxShortIn ?? '')} × ${trimZeros(draft.demyMaxLongIn ?? '')} in. `
        : `${rate.label} is now ${value} ${rate.unit}. `;
      setNotice(said + 'New costings start from this; saved ones keep what they were quoted on.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the rate');
      await load();
    } finally {
      setBusy('');
    }
  }

  const demyShort = rates.find((r) => r.key === 'demyMaxShortIn');
  const demyLong = rates.find((r) => r.key === 'demyMaxLongIn');
  const sideInput = (r: CostingRate) => (canManage ? (
    <input type="number" min="0" step="any" value={draft[r.key] ?? ''}
           aria-label={r.label} style={{ width: 70, textAlign: 'right' }}
           disabled={busy === r.key}
           onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
           onBlur={() => void save(r)}
           onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  ) : <strong>{trimZeros(r.value)}</strong>);

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          What a new costing starts from. A costing already saved keeps the
          rates it was quoted on, so changing one here never alters a price
          that has gone out. Zero means nobody has set it yet — the costing
          screen will not price a board whose paper has no rate.
        </span>
      </div>
      {demyShort && demyLong && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '12px 14px', border: '1px solid var(--line)',
                      borderRadius: 8, marginBottom: 14 }}>
          <strong style={{ marginRight: 4 }}>Demy sheet</strong>
          <span>up to</span>
          {sideInput(demyShort)}
          <span>×</span>
          {sideInput(demyLong)}
          <span>in. Anything bigger is Double Demy.</span>
        </div>
      )}
      <table className="tbl">
        <thead>
          <tr>
            <th>Rate</th>
            <th>Unit</th>
            <th className="num">Value</th>
            <th>Last changed</th>
          </tr>
        </thead>
        <tbody>
          {rates.filter((r) => !DEMY_KEYS.includes(r.key)).map((r) => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="dim">{r.unit}</td>
              <td className="num">
                {canManage ? (
                  <input type="number" min="0" step="any" value={draft[r.key] ?? ''}
                         aria-label={r.label} style={{ width: 120, textAlign: 'right' }}
                         disabled={busy === r.key}
                         onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
                         onBlur={() => void save(r)}
                         onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                ) : trimZeros(r.value)}
              </td>
              <td className="dim">
                {new Date(r.updated_at).toLocaleDateString('en-IN',
                  { day: '2-digit', month: 'short', year: '2-digit' })}
              </td>
            </tr>
          ))}
          {rates.length === 0 && (
            <tr><td colSpan={4} className="dim">No rates.</td></tr>
          )}
        </tbody>
      </table>
    </>
  );
}

/** "45.0000" reads as 45; "0.4000" as 0.4. */
function trimZeros(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

/* -------------------------------------------------------------- company */

/**
 * Who this system belongs to.
 *
 * The name and the line under it were written into two React files, so
 * changing either meant editing source and deploying. They appear in three
 * places that must agree -- the sidebar, the sign-in screen and the printed
 * costing -- and all three read this.
 */
function CompanyDetails({ canManage, setError, setNotice }: {
  canManage: boolean;
  setError: (m: string) => void;
  setNotice: (m: string) => void;
}) {
  const [company, setCompany] = useState<Company | null>(null);
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    invalidateCompany();
    try {
      const c = await loadCompany();
      setCompany(c);
      setName(c.name);
      setTagline(c.tagline);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the company details');
    }
  }, [setError]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!name.trim()) { setError('The company needs a name.'); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      await api.patch('/api/settings/company', { name: name.trim(), tagline: tagline.trim() });
      setNotice('Saved. The sidebar, the sign-in screen and printed costings all use this.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the company details');
    } finally {
      setBusy(false);
    }
  }

  async function upload(file: File) {
    setBusy(true); setError(''); setNotice('');
    try {
      // Multipart goes through fetch, as the job card's artwork does: the
      // api helper sets a JSON content type, which would break the boundary.
      const body = new FormData();
      body.append('file', file);
      const res = await fetch('/api/settings/company/logo', {
        method: 'POST', credentials: 'same-origin', body,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message ?? 'Upload failed');
      }
      setNotice('Logo updated.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload that logo');
    } finally {
      setBusy(false);
    }
  }

  const dirty = company !== null
    && (name.trim() !== company.name || tagline.trim() !== company.tagline);

  return (
    <>
      <div className="stagebar">
        <span className="hint">
          Shown in the sidebar, on the sign-in screen and at the top of every
          printed costing. A mark is optional — until one is uploaded, the
          initials of the name stand in for it.
        </span>
      </div>

      <div style={{ padding: 16, maxWidth: 560 }}>
        <div className="field">
          <label htmlFor="co-name" className="req">Company name</label>
          <input id="co-name" type="text" value={name} maxLength={120} disabled={!canManage}
                 onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="co-tag">Line underneath</label>
          <input id="co-tag" type="text" value={tagline} maxLength={120} disabled={!canManage}
                 placeholder="Corrugated Boxes"
                 onChange={(e) => setTagline(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="co-logo">Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className={`brand-mark${company?.hasLogo ? ' has-logo' : ''}`}>
              {company?.hasLogo
                ? <img src={logoUrl(company)} alt="" />
                : initialsOf(name || 'Opal')}
            </div>
            {canManage && (
              <input id="co-logo" type="file" accept="image/png,image/jpeg,image/webp"
                     disabled={busy}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       // Cleared so choosing the same file again still fires.
                       e.target.value = '';
                       if (f) void upload(f);
                     }} />
            )}
          </div>
          <div className="hint">
            PNG, JPEG or WebP, up to 1 MB. Not SVG: it can carry script, and
            serving one from our own address would be a way in.
          </div>
        </div>

        {canManage && (
          <button className="btn primary" onClick={save} disabled={busy || !dirty}>
            Save
          </button>
        )}
      </div>
    </>
  );
}
